/**
 * PublicRoute - Guard pour les routes publiques d'authentification
 *
 * Ce composant redirige vers /dashboard si l'utilisateur est déjà authentifié.
 * Utilisé pour les pages login, register, forgot-password, etc.
 */

import React, { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/hooks/redux";
import { checkAuthStatus } from "@/store/slices/authSlice";

interface PublicRouteProps {
  children: React.ReactNode;
}

// Variable globale pour éviter les vérifications multiples
let isVerifying = false;
let verificationPromise: Promise<boolean> | null = null;

export const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  // État Redux pour l'authentification user
  const {
    user,
    isAuthenticated,
    isLoading: reduxLoading,
  } = useAppSelector((state) => state.auth);

  const [isChecking, setIsChecking] = useState(true);
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Si déjà vérifié
    if (hasChecked.current) {
      setIsChecking(false);
      return;
    }

    // Si Redux indique déjà authentifié
    if (isAuthenticated && user) {
      hasChecked.current = true;
      setIsUserAuthenticated(true);
      setIsChecking(false);
      return;
    }

    const runVerification = async () => {
      // Si une vérification est déjà en cours, attendre son résultat
      if (isVerifying && verificationPromise) {
        const result = await verificationPromise;
        setIsUserAuthenticated(result);
        hasChecked.current = true;
        setIsChecking(false);
        return;
      }

      // Démarrer une nouvelle vérification
      isVerifying = true;
      verificationPromise = (async () => {
        try {
          await dispatch(checkAuthStatus()).unwrap();
          return true;
        } catch {
          return false;
        }
      })();

      try {
        const isValid = await verificationPromise;
        setIsUserAuthenticated(isValid);
        hasChecked.current = true;
      } finally {
        isVerifying = false;
        verificationPromise = null;
        setIsChecking(false);
      }
    };

    runVerification();
  }, [dispatch, isAuthenticated, user]);

  // Afficher un loader pendant la vérification
  if (isChecking || reduxLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Rediriger vers dashboard si authentifié
  if (isUserAuthenticated || isAuthenticated) {
    // Récupérer la destination originale si disponible
    const from = (location.state as { from?: { pathname: string } })?.from
      ?.pathname;
    const redirectTo = from || "/dashboard";
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

export default PublicRoute;
